/* eslint-disable no-param-reassign */
import { useEffect, useMemo, useCallback } from 'react';

/**
 * Padroniza as regras de "gate" para componentes de autocomplete com debounce.
 * - Evita flash ao limpar (valueNow < minChars => fecha e cancela request pendente)
 * - Evita buscar enquanto debouncedQuery ainda não alcançou o value (q < minChars)
 * - Respeita isLocked e canSearch
 *
 * Você decide "como fechar" (setOpen/setItems/etc.) no componente, com base em shouldClose/shouldFetch.
 */
export default function useAutocompleteQueryGate({
  value,
  debouncedQuery,
  minChars = 3,
  isLocked = false,
  canSearch = true,
  requestIdRef,
}) {
  const valueNow = useMemo(() => String(value || '').trim(), [value]);
  const q = useMemo(() => String(debouncedQuery || '').trim(), [debouncedQuery]);

  const isValueShort = valueNow.length < minChars;
  const isDebouncedShort = q.length < minChars;

  const shouldClose = isLocked || !canSearch || isValueShort || isDebouncedShort;
  const shouldFetch = !shouldClose;

  const cancelPending = useCallback(() => {
    if (requestIdRef && requestIdRef.current != null) {
      requestIdRef.current += 1;
    }
  }, [requestIdRef]);

  // Padrão ouro: ao ficar "curto" (inclui limpar), cancela request atrasada.
  useEffect(() => {
    if (isValueShort) cancelPending();
  }, [isValueShort, cancelPending]);

  return {
    valueNow,
    q,
    isValueShort,
    isDebouncedShort,
    shouldClose,
    shouldFetch,
    cancelPending,
  };
}
